import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { command } from '../api/client.js';
import { invalidate, taskResourceKey, useCommand } from '../api/resource.js';
import { spaceContextKey } from '../api/space-context.js';
import { WriteBoundary, useWriteGate } from '../api/write-boundary.js';
import { Button, Dialog, Field, Form, Textarea } from '../ui/primitives.jsx';
import { ErrorView } from '../ui/states.jsx';
import './space-context.css';

// Task 역할로 Space 권한을 추론하지 않는다. 선택한 판은 대화상자를 연 순간에 고정한다.
export function SpaceContextAdoption({ spaceId, nodeId, taskId, task, context }) {
  const [selected, setSelected] = useState(null);
  const [notice, setNotice] = useState(null);
  const trigger = useRef(null);
  const returnFocus = useRef(false);
  const granted = context.status === 'ready' && context.data.permissions.canAdopt.ok;
  const readable = task.status === 'ready' && context.status !== 'error';
  useEffect(() => {
    if (!readable || (context.status === 'ready' && !granted)) setSelected(null);
  }, [readable, granted, context.status]);
  // 성공 무효화 동안 열었던 버튼은 잠깐 disabled다. 새 읽기가 끝난 뒤 복귀한다.
  useLayoutEffect(() => {
    if (returnFocus.current && !selected && granted && task.canWrite() && context.canWrite() && trigger.current) {
      trigger.current.focus(); returnFocus.current = false;
    }
  });
  const version = task.data?.currentVersion;
  return <WriteBoundary resources={[task, context]}>
    <div class="space-context__adopt">
      {granted && version ? <Button ref={trigger} size="sm" disabled={!task.canWrite() || !context.canWrite()} onClick={() => {
        if (!task.canWrite() || !context.canWrite()) return;
        const choice = { taskId, title: task.data.title, versionNo: version.versionNo, purpose: version.purpose };
        // 먼저 낡음으로 표시한다. 대화상자 첫 렌더에서도 캐시의 개정으로 제출할 수 없다.
        invalidate(spaceContextKey(spaceId));
        invalidate(taskResourceKey(spaceId, taskId));
        setNotice(null);
        setSelected(choice);
      }}>공동 맥락으로 지정</Button> : null}
      {notice ? <p role="status" class="t-meta">{notice}</p> : null}
      {selected && readable && granted ? <AdoptionDialog spaceId={spaceId} nodeId={nodeId}
        selected={selected} context={context} task={task}
        onClose={() => { returnFocus.current = true; setSelected(null); }} onSuccess={() => {
          returnFocus.current = true; setSelected(null); setNotice('공동 맥락으로 지정했습니다. 기준과 출처는 공동 맥락에서 읽을 수 있습니다.');
        }} onPrivateFailure={() => {
          setSelected(null); setNotice('접근 상태를 확인하지 못해 선택한 내용과 입력을 닫았습니다. 다시 읽어 주세요.');
          invalidate(`space:${spaceId}`);
        }} /> : null}
    </div>
  </WriteBoundary>;
}

function AdoptionDialog({ spaceId, nodeId, selected, context, task, onClose, onSuccess, onPrivateFailure }) {
  const [rationale, setRationale] = useState('');
  const [reviewedRevision, setReviewedRevision] = useState(null);
  const [needsReview, setNeedsReview] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const submitted = useRef(null);
  const sending = useRef(false);
  const alive = useRef(true);
  const input = useRef(null);
  const gate = useWriteGate();
  useLayoutEffect(() => () => { alive.current = false; }, []);
  const adoption = useCommand((params, idempotencyKey) =>
    command('space_context_adopt', params, { nodeId, idempotencyKey }),
  { invalidates: [spaceContextKey(spaceId)] });
  const fresh = context.canWrite() && task.canWrite();
  const revision = context.data.revision;
  useEffect(() => {
    if (fresh && reviewedRevision === null && !needsReview) setReviewedRevision(revision);
  }, [fresh, revision, reviewedRevision, needsReview]);
  const changed = needsReview || (reviewedRevision !== null && reviewedRevision !== revision);
  const sameTarget = context.data.context?.taskId === selected.taskId && context.data.context?.versionNo === selected.versionNo;
  const valid = fresh && (uncertain ? submitted.current !== null : !changed && reviewedRevision !== null && !sameTarget
    && rationale.trim().length > 0 && rationale.length <= 2000);
  const submit = async () => {
    if (sending.current || !gate() || !context.canWrite() || !task.canWrite() || !valid) return;
    sending.current = true;
    const params = uncertain ? submitted.current : {
      spaceId, taskId: selected.taskId, versionNo: selected.versionNo,
      expectedRevision: reviewedRevision, rationale: rationale.trim(),
    };
    submitted.current = params;
    const result = await adoption.run(params);
    sending.current = false;
    if (!alive.current) return;
    if (result.ok) { onSuccess(); return; }
    if ([401, 403, 404].includes(result.error?.status)) { onPrivateFailure(); return; }
    if (result.error?.status === 409) {
      setNeedsReview(true); setUncertain(false); invalidate(spaceContextKey(spaceId));
    } else if (result.error) {
      const unknown = !(result.error.status >= 400 && result.error.status < 500 && result.error.code);
      setUncertain(unknown);
      if (unknown) {
        // 503도 명령 결과는 미확정이다. 키·본문은 유지하고 접근 읽기만 새로 확인한다.
        invalidate(spaceContextKey(spaceId));
        invalidate(taskResourceKey(spaceId, selected.taskId));
      }
    }
  };
  return <Dialog title="공동 맥락으로 지정" description="선택한 수락 판을 이 Space의 공동 기준으로 지정합니다."
    initialFocusRef={input} onClose={onClose} onRequestClose={() => { if (!sending.current && !uncertain) onClose(); }}>
    <Form class="stack space-context" busy={adoption.pending} onSubmit={submit}>
      <div class="stack stack--tight">
        <p class="t-body-strong">{selected.title} · v{selected.versionNo}</p>
        <p class="t-meta">선택한 수락 판의 목적</p><p class="t-body space-context__text">{selected.purpose}</p>
      </div>
      <p class="t-meta">사람이 지정한 기준이며 전체 참여자의 개별 동의를 뜻하지 않습니다. 새 판을 만들거나 제안을 수락하지 않습니다. 출처 Task의 새 판도 이 지정을 자동으로 바꾸지 않습니다.</p>
      <p class="t-meta">{context.data.context ? `현재 기준: v${context.data.context.versionNo} · 지정 이력 #${revision}` : '현재 지정된 기준이 없습니다.'}</p>
      {context.data.context ? <p class="t-meta space-context__text">현재 기준의 목적: {context.data.context.selectedVersion.purpose}</p> : null}
      {task.data.currentVersion?.versionNo !== selected.versionNo ? <p class="t-meta">출처 Task에 새 판이 있습니다. 선택한 v{selected.versionNo}은 그대로 유지됩니다.</p> : null}
      <Field label="이 기준을 함께 읽으려는 이유" htmlFor="space-context-rationale" required hint="근거와 출처는 지정 이력으로 남습니다. 최대 2,000자.">
        <Textarea ref={input} id="space-context-rationale" value={rationale} maxLength={2000}
          disabled={adoption.pending || uncertain} onInput={(event) => setRationale(event.currentTarget.value)} />
      </Field>
      {!fresh ? <p role="status" class="t-meta">최신 상태를 다시 읽는 중입니다. 입력은 유지되며 확인이 끝나야 지정할 수 있습니다.</p> : null}
      {sameTarget ? <p role="status" class="t-meta">이 판은 이미 공동 기준으로 지정되어 있습니다.{uncertain ? ' 이 읽기만으로는 보낸 요청의 처리 결과를 확정할 수 없습니다.' : ''}</p> : null}
      {changed && !uncertain ? <div class="stack stack--tight"><p role="status" class="t-meta">공동 기준이 바뀌었거나 요청이 충돌했습니다. 최신 기준을 읽고 다시 확인해 주세요. 선택한 판과 입력은 그대로입니다.</p>
        <Button size="sm" disabled={!fresh || adoption.pending || uncertain} onClick={() => {
          if (!context.canWrite() || !task.canWrite()) return;
          setReviewedRevision(revision); setNeedsReview(false); adoption.reset();
        }}>최신 기준 확인</Button></div> : null}
      {adoption.error ? <ErrorView error={adoption.error} /> : null}
      {uncertain ? <p role="status" class="t-meta">처리 결과를 확인하지 못했습니다. 같은 내용으로만 다시 확인합니다.</p> : null}
      <div class="row">
        <Button size="sm" disabled={adoption.pending || uncertain} onClick={onClose}>취소</Button>
        <Button kind="submit" size="sm" tone="human" busy={adoption.pending} disabled={!valid}>
          {uncertain ? '같은 지정 다시 확인' : '이 판을 공동 맥락으로 지정'}
        </Button>
      </div>
    </Form>
  </Dialog>;
}
